import React, { useMemo, useState } from 'react';
import { Checkmark, UploadCloud } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { Box, FormControl, FormLabel, Input, InputLabel, LinearProgress, SxProps, Theme } from '@mui/material';
import { getNetworkErrorCode, visuallyHidden } from '@utils/utils';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../context/AvContextProvider';
import { UNKNOWN_ERROR_MESSAGE } from '../hooks/useHandleError';

type Variant = 'normal' | 'compact';
const normalHeight = 270;
const heightByVariant = (variant: Variant) => ({ normal: 270, compact: 60 })[variant] || 270;

const variantStyles = (variant: Variant) =>
  ({
    normal: {
      root: {},
      previewImage: {
        width: '140px',
        height: '140px',
      },
      uploadedLabel: {
        top: 16,
        transform: 'none',
      },
      uploadingProgress: { marginTop: '-2px', width: '100%' },
      uploadingText: { height: '100%', ...flex.itemsCenter },
    },
    compact: {
      root: { ...flex.alignEnd },
      previewImage: {
        width: '60px',
        height: '60px',
      },
      uploadedLabel: {
        top: '50%',
        transform: 'translate(50%, -50%)',
      },
      uploadingProgress: { marginTop: '-2px', width: '100%', height: '100%' },
      uploadingText: { display: 'none' },
    },
  })[variant] || {};

function frameObjectByVariant(variant: Variant = 'normal') {
  return {
    normal: {
      ...flex.colCenter,
      svg: {
        height: 135,
        width: 169,
        marginBottom: '8px',
      },
    },
    compact: {
      ...flex.row,
      ...flex.justifyStartCenter,
      svg: {
        height: 56,
        width: 57,
      },
    },
  }[variant];
}

const computeStyles = (variant: Variant) => ({
  root: {
    flexDirection: 'row',
    ...variantStyles(variant).root,
  },
  outer: {
    ...flex.colCenter,
    label: {
      marginBottom: '4px',
    },
  },
  frame: (theme, isError, disabled) => ({
    ...frameObjectByVariant(variant),
    minHeight: heightByVariant(variant),
    height: heightByVariant(variant),
    border: `2px dashed ${isError ? theme.palette.error.main : theme.palette.colors.neutrals[400]}`,
    background: theme.palette.colors.neutrals[150],
    borderRadius: '2px',
    transition: theme.transitions.create(['border-color', 'background-color']),
    '&.Mui-focused': {
      borderColor: theme.palette.colors.primary[500],
      background: theme.palette.colors.primary[100],
    },
    '&:hover': {
      borderColor: !disabled && theme.palette.colors.neutrals[600],
    },
  }),
  label: {
    alignSelf: 'start',
  },
  main: {
    color: theme => theme.palette.colors.neutrals[400],
    fontSize: 24,
    lineHeight: 1.5,
    fontWeight: 600,
  },
  secondary: {
    color: theme => theme.palette.colors.neutrals[500],
  },
  browse: { color: theme => theme.palette.colors.primary[500], fontWeight: 600 },
  uploadingProgress: variantStyles(variant).uploadingProgress,
  uploadingText: variantStyles(variant).uploadingText,
  filename: { fontWeight: 600 },
  preview: {
    height: variant === 'compact' ? '100%' : normalHeight,
    paddingLeft: '24px',
    borderLeft: theme => `${variant === 'compact' ? 0 : 1}px solid ${theme.palette.colors.neutrals[400]}`,
    margin: variant === 'normal' ? 'auto 0px auto 24px' : '0',
    width: variant === 'compact' ? '140px' : '100px',
  },
  previewImage: {
    ...variantStyles(variant).previewImage,
    textAlign: 'center',
    border: theme => `1px solid ${theme.palette.colors.neutrals[300]}`,
    borderRadius: '12px',
    '& img': {
      height: '100%',
      width: '100%',
      padding: '8px',
    },
  },
  uploadedLabel: {
    alignSelf: 'flex-start',
    marginLeft: 2,
    ...flex.row,
    position: 'absolute',
    ...variantStyles(variant).uploadedLabel,
    svg: { color: theme => theme.palette.primary.main },
  },
  ellipsis: { whiteSpace: 'nowrap', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' },
});

const validateFileExtension = (type: string, filename: string): boolean =>
  FileAcceptTypes[type].split(',').includes(`.${filename.split('.').at(-1)}`);

type UploadConfigFile = { uploadedFileName: string; uploadedFileContent: string };
export type S3File = { file: File; path: string };

export interface UploadFileProps {
  label?: string;
  value?: S3File | UploadConfigFile;
  onChange: (params: S3File | UploadConfigFile) => void;
  url: string;
  isUploadConfig?: boolean;
  lastUploadedFileName?: string;
  filename: string;
  fileType?: string;
  height?: string;
  width?: string;
  error?: string;
  disabled?: boolean;
  variant?: 'normal' | 'compact';
}
const UploadFile: React.FC<UploadFileProps> = ({
  label = '',
  value,
  onChange = noop,
  url,
  isUploadConfig = false,
  lastUploadedFileName = '',
  filename,
  fileType,
  height,
  width = '450px',
  error: externalError,
  disabled = false,
  variant = 'normal',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(lastUploadedFileName);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [displayUploadedLabel, setDisplayUploadedLabel] = useState<boolean>(false);
  const { api } = useAvContext();
  const styles = useMemo(() => computeStyles(variant), [variant]);
  const upload = async (file: File) => {
    setError(undefined);
    try {
      if (validateFileExtension(fileType!, file.name)) {
        const formData = new FormData();
        formData.append(filename, file);
        onChange(file as any);
        setUploadedFileName(file.name);
        setIsLoading(true);
        await api(url, {
          options: { method: 'POST' },
          body: formData,
          isJson: false,
          muteErrors: true,
          onSuccess: ({ path, uploadedFileContent }) => {
            setIsLoading(false);
            setDisplayUploadedLabel(true);
            setTimeout(() => setDisplayUploadedLabel(false), 2000);
            onChange(isUploadConfig ? { uploadedFileName: file.name, uploadedFileContent } : { file, path });
          },
        });
      } else {
        const validationError = new Error('Invalid file type') as any;
        validationError.status = 400;
        throw validationError;
      }
    } catch (e: any) {
      setUploadedFileName('');
      setIsLoading(false);
      setDisplayUploadedLabel(false);
      if ([400, 413].includes(getNetworkErrorCode(e))) {
        setError(e.message);
      } else {
        console.error(e);
      }
      enqueueSnackbar(typeof e?.message === 'string' ? e?.message : UNKNOWN_ERROR_MESSAGE, {
        variant: 'error',
      });
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    setDragOver(false);
    event.preventDefault();
    upload(event.dataTransfer.files[0]);
  };

  const onDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    setDragOver(true);
    event.preventDefault();
  };

  const isError = Boolean(error || externalError);

  const InitialStateWrapper =
    variant === 'compact'
      ? ({ children }) => (
          <Box sx={{ ...flex.center }}>
            <Box
              sx={{
                ...flex.row,
                alignItems: 'baseline',
                // alignItems: 'end',
              }}>
              {children}
            </Box>
          </Box>
        )
      : ({ children }) => <Box sx={{ ...flex.colCenter }}>{children}</Box>;
  return (
    <FormControl sx={styles.root} error={isError} disabled={disabled}>
      <Box sx={styles.outer}>
        <InputLabel sx={styles.label}>{label}</InputLabel>
        <FormLabel
          error={isError}
          sx={(theme => ({ ...styles.frame(theme, isError, disabled), height, width })) as SxProps<Theme>}
          className={dragOver ? 'Mui-focused' : ''}
          onDrop={handleDrop}
          onDragOver={onDragOver}
          onDragLeave={() => setDragOver(false)}>
          {isLoading ? (
            <>
              <Box sx={styles.uploadingProgress}>
                <LinearProgress />
                <Box sx={{ margin: 2, ...styles.ellipsis }}>
                  Uploading file{' '}
                  <Box component="span" sx={styles.filename}>
                    {uploadedFileName}
                  </Box>
                </Box>
              </Box>
              <Box sx={styles.uploadingText}>
                <Box sx={{ ...styles.main, ...styles.uploadingText }}>UPLOADING...</Box>
              </Box>
            </>
          ) : (
            <Box
              sx={
                variant === 'compact'
                  ? { ...flex.row, ...flex.justifyCenter, gap: '10px', ml: '10px' }
                  : { width: '100%', ...flex.colCenter }
              }>
              {uploadedFileName && (displayUploadedLabel || variant === 'normal') && (
                <Box sx={{ ...styles.uploadedLabel }}>
                  <Checkmark style={{ height: 20, width: 20 }} />
                  <Box sx={{ marginX: 2, ...styles.ellipsis }}>
                    Uploaded{' '}
                    <Box component="span" sx={styles.filename}>
                      {uploadedFileName}
                    </Box>
                  </Box>
                </Box>
              )}
              <Input
                inputProps={{ accept: FileAcceptTypes[fileType!] }}
                onChange={({ target: { files } }: any) => upload(files[0])}
                type="file"
                sx={visuallyHidden}
              />
              {!displayUploadedLabel && <UploadCloud />}
              {!disabled && !displayUploadedLabel && (
                <InitialStateWrapper>
                  <Box sx={{ ...styles.main, ...(variant === 'compact' ? { fontSize: '16px' } : {}) }}>DRAG & DROP</Box>
                  <Box sx={{ ...styles.secondary, marginInlineStart: variant === 'compact' ? '1ch' : '0' }}>
                    files here, or{' '}
                    <Box component="span" sx={styles.browse}>
                      Browse
                    </Box>
                  </Box>
                </InitialStateWrapper>
              )}
            </Box>
          )}
        </FormLabel>
        <Box sx={{ ...styles.secondary, alignSelf: 'start', fontSize: 12 }}>Accepted File Types: {fileType}</Box>
      </Box>

      {(value as S3File)?.path || (value as S3File)?.file ? (
        <Box sx={styles.preview}>
          <InputLabel focused={false} sx={{ mb: 0.5 }}>
            Preview
          </InputLabel>
          <Box sx={styles.previewImage}>
            <img src={(value as S3File).file ? URL.createObjectURL((value as S3File).file) : `/${(value as S3File).path}`} alt="preview" />
          </Box>
        </Box>
      ) : (
        ''
      )}
    </FormControl>
  );
};

export default UploadFile;

export const UploadFileType = {
  Image: 'JPG, GIF, PNG',
  Data: 'JSON, JSONL, CSV, XLSX, ZIP, XML, ZST, ZSTD, PARQUET',
  Config: 'JSON, PEM',
};

const FileAcceptTypes = {
  [UploadFileType.Image]: '.jpg,.png,.jpeg,.svg',
  [UploadFileType.Data]: '.zst,.zstd,.zip,.json,.jsonl,.csv,.xlsx,.xml,.nessus,.parquet',
  [UploadFileType.Config]: '.json,.pem',
};
